import { TranslatePipe } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { Apollo } from "apollo-angular";
import { map } from "rxjs";

import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { MatDialog } from "@angular/material/dialog";
import { MatSortModule, Sort } from "@angular/material/sort";
import { MatTooltipModule } from "@angular/material/tooltip";

import { IpPrefixDdosType, IpPrefixDdosTypeConnectionV2 } from "../../../apis/networkdashboard/graphql/graphql";
import { CERT_MITIGATION_PREFIXES_QUERY } from "../../../apis/networkdashboard/static/graphql-queries/cert-mitigation";
import { registerDirtyRefresher } from "../../../helpers/dirty-subscription-refresher";
import { EnabledPipe } from "../../../helpers/enabledpipe/enabledpipe";
import { normalizePrefix } from "../../../helpers/ip";
import { AuthService } from "../../../services/authservice";
import { LoadingState } from "../../../services/loading-state";
import { LoaderComponent } from "../../common/loader/loader";
import { DialogAuthComponent } from "../../dialogs/dialog-auth/dialog-auth";
import { openSelfServiceDialog } from "../../dialogs/dialog-selfservice/dialog-selfservice.helpers";
import { SelfserviceToggle } from "../../selfservice/selfservice-toggle/selfservice-toggle";
import { IpVersionPipe } from "./ip-version-pipe";

@Component({
  selector: "cert-mitigation",
  templateUrl: "cert-mitigation.html",
  styleUrl: "cert-mitigation.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AngularSvgIconModule,
    EnabledPipe,
    IpVersionPipe,
    LoaderComponent,
    MatSortModule,
    MatTooltipModule,
    SelfserviceToggle,
    TranslatePipe,
  ],
})
export class CertMitigation {
  protected auth = inject(AuthService);
  protected loadingState = inject(LoadingState);

  protected sortedData = computed(() => this.sortedSubscriptions());
  protected allowSelfService = signal(false);
  protected loading = computed(() => this.subscriptions.isLoading());

  private dialog = inject(MatDialog);
  private apollo = inject(Apollo);
  private subscriptions = rxResource<IpPrefixDdosType[], string>({
    params: () => this.auth.viewingCustomer().customerId,
    stream: ({ params: customerId }) =>
      this.apollo
        .query<{ ipPrefixDdos: IpPrefixDdosTypeConnectionV2 }>({
          query: CERT_MITIGATION_PREFIXES_QUERY,
          variables: { customer: customerId },
          fetchPolicy: "network-only",
        })
        .pipe(map(({ data }) => data!.ipPrefixDdos.page as IpPrefixDdosType[])),
  });
  private activeSort = signal<Sort>({ active: "description", direction: "asc" });

  private sortedSubscriptions = computed(() => {
    const subscriptions = [...(this.subscriptions.value() ?? [])];
    if (!this.activeSort().active || this.activeSort().direction === "") {
      return subscriptions;
    }
    const isAsc = this.activeSort().direction === "asc";
    return subscriptions.sort((a, b) => {
      switch (this.activeSort().active) {
        case "description":
          return this.compare(a.description, b.description, isAsc);
        case "prefix":
          return this.compare(normalizePrefix(a.prefix), normalizePrefix(b.prefix), isAsc);
        case "version":
          return this.compare(a.afi, b.afi, isAsc);
        default:
          return 0;
      }
    });
  });

  constructor() {
    const roleCheckResult = this.auth.checkRoleAccess("cert", "edit");
    this.allowSelfService.set(this.auth.isSelfServiceEnabled && roleCheckResult.ok);
    registerDirtyRefresher(
      computed(() => this.subscriptions.value()?.some((s) => s.dirty === true) ?? false),
      () => this.subscriptions.reload(),
    );
    effect(() => {
      this.loadingState.total = this.subscriptions.value()?.length ?? 0;
    });
  }

  protected changeSort(sort: Sort) {
    this.activeSort.set(sort);
  }

  private compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  protected toggleClick(subscription: IpPrefixDdosType) {
    // update automitigation
    if (!subscription.dirty) {
      this.openSelfserviceDialog(subscription);
    }
  }

  private openSelfserviceDialog(subscription: IpPrefixDdosType) {
    const roleCheckResult = this.auth.checkRoleAccess("cert", "edit");
    if (!roleCheckResult.ok) {
      this.auth.roleEvent.emit(roleCheckResult);
      return;
    }

    if (!this.auth.selfServiceStarted()) {
      const data = { selfserviceState: { subscription }, initialAction: "autoMitigation" };
      this.dialog.open(DialogAuthComponent, { data });
    } else {
      const type = "autoMitigation";
      // TODO: typing gone terribly wrong. Adding useless typing that could be inferred, just because openSelfServiceDialog
      // accepts a vague union type is not a good idea. Probably a better idea to have specific self service dialogs
      const mitigationState: "enabled" | "disabled" = subscription.autoMitigation ? "enabled" : "disabled";
      const product: { type: "IpPrefixDdos"; tag: "" } = { type: "IpPrefixDdos", tag: "" };
      const subscriptionWithProduct = { ...subscription, product };

      const data = { type, subscription: subscriptionWithProduct, mitigationState };
      const dialogRef = openSelfServiceDialog(this.dialog, data);
      dialogRef.afterClosed().subscribe((event: string) => {
        if (event === "refresh") {
          // perform some optimistic rendering here
          // assume the toggle for mitigationState is successful.
          this.optimisticResult(subscription.subscriptionId, !subscription.autoMitigation);
        }
      });
    }
  }

  private optimisticResult(subscriptionId: string, _mitigationState: boolean) {
    const current = this.subscriptions.value() ?? [];
    const index = current.findIndex((s) => s.subscriptionId === subscriptionId);
    const updated = [...current.filter((s) => s.subscriptionId !== subscriptionId), { ...current[index], dirty: true }];
    this.subscriptions.set(updated);
  }
}
